// frontend/src/legal/legalFragment.ts — gemeinsamer Unterbau fuer die beiden
// Stellen, die ein Rechtstext-Fragment (GET /api/legal/:doc) rendern:
//   - das In-App-Pop-Up  frontend/src/components/ui/LegalDialog.tsx
//   - die Vollroute      frontend/src/pages/legal/index.tsx  (/legal/:doc)
//
// R9-05 (Ursache des Befunds "Schalter Vollstaendig wirkt nicht"):
// Der Datenschutz-Umschalter "Einfache Sprache | Vollstaendig" haengt im
// statischen public/datenschutz.html an einem INLINE-SCRIPT. Der Fragment-
// Endpunkt entfernt serverseitig alle <script> (server/legal-fragment.js,
// bewusst, CSP), liefert die Buttons und beide Fassungen aber mit — genau
// im Ausgangszustand (simple sichtbar, full hidden). Den Ersatz-Listener gab
// es bisher NUR im Pop-Up (C-09, LegalDialog.tsx). Auf der Vollroute
// /legal/datenschutz — der Seite, die Julius geoeffnet hat — wurde das
// Fragment ohne jeden Listener eingehaengt: Die Buttons waren tote Knoepfe,
// die Vollfassung blieb dauerhaft `hidden`, und der Hinweis "Du liest gerade
// die kindgerechte Kurzfassung" blieb stehen. Beide Fassungen stehen dabei
// im DOM (die Vollfassung eben unsichtbar) — daher der Eindruck, es sei
// "beides da, aber das Falsche sichtbar".
//
// Die Logik liegt jetzt EINMAL hier und wird von beiden Stellen genutzt,
// damit sie nicht wieder auseinanderlaufen kann.

import { useEffect } from 'react';

/** Im Fragment erlaubte Lesefassungen (data-version aus public/datenschutz.html). */
export type LegalVersion = 'simple' | 'full';

function isLegalVersion(value: string | null): value is LegalVersion {
  return value === 'simple' || value === 'full';
}

// Audit 05-P2-21 (Defense-in-depth): DOMPurify ist im Projekt NICHT als
// Dependency vorhanden (package.json, node_modules, imports) — die Treffer
// in public/js/*.js beziehen sich auf eine runtime-CDN-Variante (`typeof
// DOMPurify !== 'undefined'`), die im React-SPA-Bundle nicht verfuegbar ist.
// Daher KEINE neue npm-Dependency, sondern eine kleine, konservative
// Regex-Sanitisierung als zweite Verteidigungslinie VOR dem dangerouslySetInnerHTML.
// Primaere Verteidigung bleibt der Server (legal-fragment.js strippt bereits alle
// <script>-Tags und liefert nur First-Party-public/*.html aus einer Whitelist).
// Diese Funktion ist explizit defensiv: lieber zu viel Markup entschaerfen
// (z.B. Attribute mit aehnlichem Namen) als einen XSS-Vektor durchlassen.
// Entfernt werden:
//   1. <script>...</script>-Bloecke (mit/ohne Attribute, inkl. self-closing
//      und orphan-Tag ohne schliessendes </script>).
//   2. on*-Event-Handler-Attribute (onclick, onerror, onload, ...).
//      Whitespace/"'/ vor "on" als Boundary, damit "data-onclick" o.ae.
//      zufaellige Treffer ausgeschlossen werden.
//   3. javascript:-URLs in href/src/action/formaction/xlink:href — inkl.
//      Whitespace/Newline-Tricks ("java\tscript:") und HTML-Entity-Variante
//      ("java&#x09;script:"). Ersatz: about:blank (laedt nichts, macht den
//      Link inert).
// Legitime Rechts-Text-Formatierung (h1-h6, p, ul/ol/li, a[href=mailto:|https:],
// strong, em, br, hr, blockquote, code, pre, span, div, table, ...) bleibt
// unangetastet, weil nur die drei genannten Muster entfernt werden.
export function sanitizeLegalHtml(input: string): string {
  if (!input) return '';
  let html = input;
  // 1a. Vollstaendige <script>...</script>-Bloecke (non-greedy, multiline).
  html = html.replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, '');
  // 1b. Uebrig gebliebene oeffnende/standalone <script>-Tags defensiv kicken
  //     (sollte nach 1a nicht mehr vorkommen, aber billig).
  html = html.replace(/<script\b[^>]*\/?>/gi, '');
  // 2. on*-Event-Handler (quoted oder bare). Boundary [\s/"'<] schliesst
  //    false-positives wie data-onclick=, longitude= etc. zuverlaessig aus.
  html = html.replace(/(^|[\s/"'<])on[a-z][a-z0-9]*\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>"']+)/gi, '$1');
  // 3. javascript:-URLs (inkl. \s/\n-Tricks und &#x0?HEX;-, &#DEC;-Entities).
  //    Wird auf about:blank ersetzt → Link bleibt anklickbar, aber inert.
  html = html.replace(
    /(\b(?:href|src|action|formaction|xlink:href)\s*=\s*["']?)\s*(?:java&#x0*[a-z0-9]*;?|javascript[\s\n\r]*:)/gi,
    '$1about:blank',
  );
  return html;
}

/**
 * Schaltet innerhalb eines gerenderten Rechtstext-Fragments auf die
 * gewuenschte Lesefassung um: genau eine .legal-version-content ist danach
 * sichtbar, die Buttons tragen aria-pressed/is-active passend dazu, und der
 * zugehoerige Hinweistext wird ein-, der andere ausgeblendet.
 *
 * Exportiert, damit der Test die reine DOM-Umschaltung ohne React pruefen kann.
 */
export function applyLegalVersion(root: ParentNode, version: LegalVersion): void {
  // 1. Inhalte: nur die passende Fassung bleibt sichtbar (hidden-Attribut).
  //    Wichtig: hidden-ATTRIBUT statt style.display — der globale Reset der
  //    Rechtstext-Seiten kennt kein [hidden]{display:none!important}, das
  //    UA-Stylesheet setzt display:none, und ein spaeteres CSS koennte ein
  //    inline gesetztes display sonst nicht mehr ueberstimmen.
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('.legal-version-content'))) {
    if (el.getAttribute('data-version') === version) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  // 2. Buttons: aria-pressed + is-active-Klasse konsistent setzen.
  for (const b of Array.from(root.querySelectorAll<HTMLButtonElement>('.legal-version-btn'))) {
    const isActive = b.getAttribute('data-version') === version;
    b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    b.classList.toggle('is-active', isActive);
  }

  // 3. Hinweise: passend zur aktiven Fassung ein-/ausblenden.
  const hintSimple = root.querySelector<HTMLElement>('#legal-version-hint-simple');
  const hintFull = root.querySelector<HTMLElement>('#legal-version-hint-full');
  if (hintSimple) hintSimple.toggleAttribute('hidden', version !== 'simple');
  if (hintFull) hintFull.toggleAttribute('hidden', version !== 'full');
}

/**
 * Haengt die Klick-Delegation fuer den Datenschutz-Versionsumschalter an den
 * Container, in den das Fragment gerendert wurde.
 *
 * CSP-sicher: kein inline-Script, nur addEventListener am Container-Element.
 * Bei Dokumenten ohne Umschalter (impressum/agb/nutzungsbedingungen) fehlen
 * die Buttons → die Delegation ist ein No-Op.
 *
 * Der Standard bleibt bewusst die Kurzfassung, so wie sie im HTML steht
 * (Minderjaehrigen-Schutz). Wir setzen hier nichts vor, sondern reparieren
 * ausschliesslich den Wechsel.
 *
 * R9-05-Review (FE-01): Der Hook nimmt das ELEMENT, nicht ein Ref-Objekt.
 * Grund: ResponsiveDialog rendert je nach Breite <Sheet> oder <Modal> — zwei
 * verschiedene Komponententypen. Kippt der Viewport bei offenem Pop-Up ueber
 * die 600-px-Schwelle (Foldable, iPad-Split-View, Desktop-Fenster), mountet
 * React den gesamten Dialog-Teilbaum neu: der Content-<div> ist danach ein
 * NEUER DOM-Knoten. Ein Ref-Objekt bleibt dabei identisch (nur .current
 * wechselt), ein Effekt mit dem Ref als Dependency laeuft also nicht erneut —
 * der Listener haengt am abgehaengten alten Knoten, beide Knoepfe sind tot.
 * Mit dem Element als State (`ref={setRoot}`) liefert der Remount einen neuen
 * Wert, der Effekt raeumt den alten Listener ab und haengt sich neu an.
 *
 * @param root  Container mit dem eingehaengten Fragment (null, solange das
 *              Fragment nicht gerendert ist — z.B. status !== 'loaded')
 * @param html  aktuelles Fragment — erzwingt ein Neu-Anhaengen nach Nachladen
 */
export function useLegalVersionSwitch(
  root: HTMLElement | null,
  html: string,
): void {
  useEffect(() => {
    if (!root) return undefined;

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const btn = target.closest<HTMLButtonElement>('.legal-version-btn');
      if (!btn || !root.contains(btn)) return;

      // data-version entscheidet, welche Fassung sichtbar wird. Erwartet
      // "simple" oder "full" (aus public/datenschutz.html); defensiv:
      // alles andere ignorieren.
      const nextVersion = btn.getAttribute('data-version');
      if (!isLegalVersion(nextVersion)) return;

      applyLegalVersion(root, nextVersion);
    };

    root.addEventListener('click', onClick);
    return () => {
      root.removeEventListener('click', onClick);
    };
    // root wechselt bei jedem Remount des Containers (Sheet↔Modal); html
    // triggert ein Neu-Anhaengen nach Dokument- oder Sprachwechsel.
  }, [root, html]);
}
